import { useState, useRef, useEffect } from 'react';
import { BookOpen, X, Upload, Loader2, Check, ImageIcon, Download, AlertTriangle, ScanLine, CloudDownload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAppContext } from '@/hooks/useAppContext';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import { uploadWithProgress, uploadFileToAllServers, formatBytes } from '@/lib/blossomUpload';
import { extractPdfCover } from '@/lib/pdfCover';
import { fetchPdfCached } from '@/lib/pdfCache';
import { KIND_MAGAZINE_ISSUE } from '@/lib/pressstrKinds';
import type { NostrEvent } from '@nostrify/nostrify';

interface EditIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: NostrEvent;
  onSuccess?: () => void;
}

interface SaveProgress {
  stage: 'idle' | 'uploading-pdf' | 'uploading-cover' | 'publishing' | 'complete' | 'error';
  message: string;
  progress: number;
  fileLoaded?: number;
  fileTotal?: number;
  error?: string;
}

export function EditIssueDialog({ open, onOpenChange, issue, onSuccess }: EditIssueDialogProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();

  const slug = issue.tags.find(([n]) => n === 'd')?.[1] ?? '';
  const aTag = issue.tags.find(([n]) => n === 'a')?.[1];

  const [title, setTitle] = useState('');
  const [issueId, setIssueId] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [publishedAt, setPublishedAt] = useState('');

  // PDF state
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');   // existing URL shown when no new file
  const [pdfSha, setPdfSha] = useState('');
  const [pdfSize, setPdfSize] = useState('');

  // Cover state
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState('');
  const [coverAutoExtracted, setCoverAutoExtracted] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);

  const [saveProgress, setSaveProgress] = useState<SaveProgress>({ stage: 'idle', message: '', progress: 0 });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLoaded, setDownloadLoaded] = useState(0);
  const [downloadTotal, setDownloadTotal] = useState(0);

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTitle(issue.tags.find(([n]) => n === 'title')?.[1] ?? '');
      setIssueId(issue.tags.find(([n]) => n === 'issue')?.[1] ?? '');
      setSummary(issue.tags.find(([n]) => n === 'summary')?.[1] ?? '');
      setContent(issue.content ?? '');
      setPdfUrl(issue.tags.find(([n]) => n === 'url')?.[1] ?? '');
      setPdfSha(issue.tags.find(([n]) => n === 'x')?.[1] ?? '');
      setPdfSize(issue.tags.find(([n]) => n === 'size')?.[1] ?? '');
      setPdfFile(null);
      setCoverUrl(issue.tags.find(([n]) => n === 'image')?.[1] ?? '');
      setCoverFile(null);
      setCoverPreview(null);
      setCoverAutoExtracted(false);
      setIsExtracting(false);
      setIsDownloading(false);
      setDownloadLoaded(0);
      setDownloadTotal(0);
      const pa = issue.tags.find(([n]) => n === 'published_at')?.[1];
      setPublishedAt(pa ? new Date(parseInt(pa) * 1000).toISOString().slice(0, 10) : '');
      setSaveProgress({ stage: 'idle', message: '', progress: 0 });
    }
  }, [open, issue]);

  const handlePdfSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast({ title: 'Invalid file', description: 'Please select a PDF file.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setPdfFile(cloned);
    e.target.value = '';

    // Auto-extract new cover from first page, replacing existing
    await doExtractCover(cloned);
  };

  const doExtractCover = async (pdfSource?: File) => {
    const src = pdfSource ?? pdfFile;
    if (!src) {
      toast({ title: 'No PDF selected', description: 'Select a PDF first.', variant: 'destructive' });
      return;
    }
    setIsExtracting(true);
    try {
      const cover = await extractPdfCover(src);
      setCoverFile(cover);
      setCoverPreview(URL.createObjectURL(cover));
      setCoverAutoExtracted(true);
    } catch (err) {
      toast({
        title: 'Cover extraction failed',
        description: err instanceof Error ? err.message : 'Could not render PDF page 1.',
        variant: 'destructive',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const doExtractFromUrl = async () => {
    if (!pdfUrl) {
      toast({ title: 'No PDF URL', description: 'This issue has no PDF URL yet.', variant: 'destructive' });
      return;
    }
    setIsDownloading(true);
    setDownloadLoaded(0);
    setDownloadTotal(0);
    try {
      const file = await fetchPdfCached(pdfUrl, (loaded, total) => {
        setDownloadLoaded(loaded);
        setDownloadTotal(total);
      });
      await doExtractCover(file);
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : 'Could not download PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCoverSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { e.target.value = ''; return; }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setCoverFile(cloned);
    setCoverPreview(URL.createObjectURL(cloned));
    setCoverAutoExtracted(false);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!title.trim() || !issueId.trim()) {
      toast({ title: 'Missing fields', description: 'Title and issue number are required.', variant: 'destructive' });
      return;
    }
    if (!user) return;

    const servers = getEffectiveBlossomServers(config.blossomServerMetadata, config.useAppBlossomServers);
    if ((pdfFile || coverFile) && servers.length === 0) {
      toast({ title: 'No Blossom servers', description: 'Configure Blossom servers in settings.', variant: 'destructive' });
      return;
    }

    setSaveProgress({ stage: pdfFile ? 'uploading-pdf' : coverFile ? 'uploading-cover' : 'publishing', message: pdfFile ? 'Uploading PDF to Blossom…' : coverFile ? 'Uploading cover image…' : 'Publishing to Nostr…', progress: 10, fileLoaded: pdfFile ? 0 : undefined, fileTotal: pdfFile?.size });

    try {
      let finalPdfUrl = pdfUrl;
      let finalSha = pdfSha;
      let finalSize = pdfSize;

      if (pdfFile) {
        const result = await uploadWithProgress(pdfFile, servers, user.signer, ({ loaded, total }) => {
          setSaveProgress(prev => ({ ...prev, fileLoaded: loaded, fileTotal: total }));
        });
        finalPdfUrl = result.url;
        finalSha = result.sha256;
        finalSize = String(pdfFile.size);
      }

      let finalCoverUrl = coverFile ? '' : coverUrl;
      if (coverFile) {
        setSaveProgress({ stage: 'uploading-cover', message: 'Uploading cover image…', progress: 80 });
        const result = await uploadFileToAllServers(coverFile, servers, user.signer);
        finalCoverUrl = result.url;
      }

      setSaveProgress({ stage: 'publishing', message: 'Publishing to Nostr…', progress: 92 });

      const eventTags: string[][] = [
        ['d', slug],
        ['title', title.trim()],
        ['issue', issueId.trim()],
        ['url', finalPdfUrl],
        ['m', 'application/pdf'],
        ['alt', `Magazine Issue: ${title.trim()}, Issue ${issueId.trim()}`],
      ];
      if (aTag) eventTags.push(['a', aTag]);
      if (finalSha) eventTags.push(['x', finalSha]);
      if (finalSize) eventTags.push(['size', finalSize]);
      if (finalCoverUrl) eventTags.push(['image', finalCoverUrl]);
      if (summary.trim()) eventTags.push(['summary', summary.trim()]);
      if (publishedAt) {
        const ts = Math.floor(new Date(publishedAt).getTime() / 1000);
        if (!isNaN(ts)) eventTags.push(['published_at', String(ts)]);
      }

      await publishEvent({ kind: KIND_MAGAZINE_ISSUE, content: content.trim(), tags: eventTags });
      setSaveProgress({ stage: 'complete', message: 'Issue updated!', progress: 100 });
      toast({ title: 'Issue updated!' });
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSaveProgress({ stage: 'error', message: 'Save failed', progress: 0, error: msg });
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const busy = saveProgress.stage !== 'idle' && saveProgress.stage !== 'error' || isPublishing || isDownloading;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Edit Issue
          </DialogTitle>
          <DialogDescription>
            Update this issue. You can replace the PDF — a new cover will be auto-extracted from it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* PDF file */}
          <div>
            <Label className="text-sm font-medium">PDF File</Label>
            <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={handlePdfSelect} className="hidden" />
            {pdfFile ? (
              <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <Download className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pdfFile.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(pdfFile.size)} · new file</p>
                </div>
                <button onClick={() => { setPdfFile(null); setCoverFile(null); setCoverPreview(null); setCoverAutoExtracted(false); }} className="p-1 hover:text-destructive shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <Download className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{pdfUrl || 'No file'}</p>
                  <p className="text-xs text-muted-foreground">Current PDF</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => pdfInputRef.current?.click()} className="shrink-0">
                  <Upload className="w-3.5 h-3.5 mr-1" />Replace
                </Button>
              </div>
            )}
          </div>

          {/* Cover */}
          <div>
            <Label className="text-sm font-medium">Cover Image</Label>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
            <div className="mt-2 flex items-start gap-4">
              {coverPreview ? (
                <div className="relative w-20 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <button onClick={() => { setCoverFile(null); setCoverPreview(null); setCoverAutoExtracted(false); }} className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white"><X className="w-3 h-3" /></button>
                </div>
              ) : coverUrl ? (
                <div className="relative w-20 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0 cursor-pointer" onClick={() => coverInputRef.current?.click()}>
                  <img src={coverUrl} alt="Current cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Upload className="w-4 h-4 text-white" />
                  </div>
                </div>
              ) : (
                <button onClick={() => coverInputRef.current?.click()} className="w-20 aspect-[2/3] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-muted/50 transition-colors shrink-0">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <div className="flex-1 pt-1 space-y-2">
                {coverAutoExtracted && (
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />Extracted from PDF page 1
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Replace the cover image or extract from the PDF.</p>
                {/* Download progress when fetching PDF from URL */}
                {isDownloading && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      {downloadTotal > 0
                        ? `Downloading… ${Math.round((downloadLoaded / downloadTotal) * 100)}%`
                        : 'Downloading PDF…'}
                    </p>
                    {downloadTotal > 0 && (
                      <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-150"
                          style={{ width: `${Math.round((downloadLoaded / downloadTotal) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {pdfFile ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={isExtracting || busy}
                      onClick={() => doExtractCover()}
                    >
                      {isExtracting
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Extracting…</>
                        : <><ScanLine className="w-3 h-3 mr-1" />Extract from PDF</>
                      }
                    </Button>
                  ) : pdfUrl ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={isExtracting || busy}
                      onClick={doExtractFromUrl}
                    >
                      {isDownloading || isExtracting
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{isDownloading ? 'Downloading…' : 'Extracting…'}</>
                        : <><CloudDownload className="w-3 h-3 mr-1" />Extract from current PDF</>
                      }
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    disabled={busy}
                    onClick={() => coverInputRef.current?.click()}
                  >
                    Upload image
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Progress bars (shown during save) */}
          {saveProgress.stage !== 'idle' && saveProgress.stage !== 'complete' && saveProgress.stage !== 'error' && (
            <div className="space-y-3 p-3 rounded-lg bg-muted/40">
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{saveProgress.message}</span>
                  <span className="font-medium">{saveProgress.progress}%</span>
                </div>
                <Progress value={saveProgress.progress} />
              </div>
              {saveProgress.fileTotal !== undefined && saveProgress.fileTotal > 0 && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Upload className="w-3 h-3" />File upload</span>
                    <span className="tabular-nums">
                      {formatBytes(saveProgress.fileLoaded ?? 0)} / {formatBytes(saveProgress.fileTotal)}
                      {' '}
                      <span className="text-foreground font-medium">
                        ({Math.round(((saveProgress.fileLoaded ?? 0) / saveProgress.fileTotal) * 100)}%)
                      </span>
                    </span>
                  </div>
                  <Progress value={Math.round(((saveProgress.fileLoaded ?? 0) / saveProgress.fileTotal) * 100)} className="h-2" />
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
                <Loader2 className="w-3 h-3 animate-spin" />Please wait…
              </div>
            </div>
          )}

          {saveProgress.stage === 'error' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{saveProgress.error}</AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="edit-issue-title">Issue Title *</Label>
            <Input id="edit-issue-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-issue-num">Issue Number / Identifier *</Label>
            <Input id="edit-issue-num" value={issueId} onChange={(e) => setIssueId(e.target.value)} className="mt-1.5" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-issue-date">Original Publication Date</Label>
            <Input id="edit-issue-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className="mt-1.5 w-48" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-issue-summary">Summary / Editorial Note</Label>
            <Input id="edit-issue-summary" value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1.5" disabled={busy} />
          </div>
          <div>
            <Label htmlFor="edit-issue-content">Table of Contents / Notes (Markdown)</Label>
            <Textarea id="edit-issue-content" value={content} onChange={(e) => setContent(e.target.value)} rows={3} className="mt-1.5" disabled={busy} />
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} className="flex-1" disabled={busy || !title.trim() || !issueId.trim()}>
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
